/** Lese-Rückblick.
 *
 *  Ehrlich gehalten: Die Lesedaten in beiden Exporten sind lückenhaft (bei Mario
 *  sind nur 6 von 89 Lesedaten gefüllt, bei Sarah trugen 96 Zeilen einen
 *  Platzhalter). Deshalb steht die Zahl der Bücher ohne Datum immer daneben,
 *  statt eine vollständige Lesebiografie vorzutäuschen.
 */

import { useEffect, useMemo, useState } from "react";
import type { Stats } from "../../../shared/types";
import { OWNER_NAME } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

function Balken({ jahre }: { jahre: { year: string; count: number }[] }) {
  const max = Math.max(1, ...jahre.map((j) => j.count));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        height: 180,
        marginTop: 20,
      }}
    >
      {jahre.map((j) => (
        <div
          key={j.year}
          style={{ flex: 1, textAlign: "center", minWidth: 34 }}
        >
          <div
            title={`${j.count} Bücher`}
            style={{
              height: `${(j.count / max) * 140}px`,
              background:
                "linear-gradient(180deg, rgba(210,174,110,0.85), rgba(210,174,110,0.28))",
              borderRadius: "1px 1px 0 0",
            }}
          />
          <div
            className="klein"
            style={{ fontSize: 9, marginTop: 6, letterSpacing: "0.1em" }}
          >
            {j.year}
          </div>
          <div
            className="klein"
            style={{ fontSize: 11, letterSpacing: 0, color: "var(--paper)" }}
          >
            {j.count}
          </div>
        </div>
      ))}
    </div>
  );
}

function Kennzahl({ wert, name }: { wert: string | number; name: string }) {
  return (
    <div className="karte" style={{ minWidth: 150, flex: "1 1 150px" }}>
      <div
        style={{ fontFamily: "var(--display)", fontSize: 30, lineHeight: 1.1 }}
      >
        {wert}
      </div>
      <div className="klein" style={{ marginTop: 6 }}>
        {name}
      </div>
    </div>
  );
}

export function Rueckblick() {
  const { owner, books, modus } = useStore();
  const [werte, setWerte] = useState<Stats | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (modus !== "server") {
      setFehler(
        "Ohne Server werden die Zahlen aus der Vorschau berechnet — unvollständig.",
      );
      return;
    }
    api
      .stats(owner)
      .then((res) => setWerte(res.data))
      .catch((err) => setFehler(err.message));
  }, [owner, modus]);

  // Ersatzrechnung für die Vorschau, damit die Seite nie leer bleibt
  const ersatz = useMemo(() => {
    const jahre = new Map<string, number>();
    for (const b of books)
      if (b.read_year)
        jahre.set(b.read_year, (jahre.get(b.read_year) || 0) + 1);
    return {
      total: books.length,
      byYear: [...jahre.entries()]
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => a.year.localeCompare(b.year)),
      noYear: books.filter((b) => !b.read_year).length,
    };
  }, [books]);

  const jahre = werte?.byYear ?? ersatz.byYear;
  const gesamt = werte?.total ?? ersatz.total;
  const ohneJahr = werte?.noYear ?? ersatz.noYear;

  return (
    <>
      <section style={{ maxWidth: "46ch" }}>
        <h2 className="titelzeile">Rückblick</h2>
        <p className="lauftext">
          Was {OWNER_NAME[owner]} gelesen hat, über die Jahre. Die Lesedaten
          stammen aus dem Export und sind lückenhaft — was kein Datum hat, steht
          unten gesondert.
        </p>
      </section>

      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--warn)", marginTop: 20 }}
        >
          {fehler}
        </div>
      )}

      <div
        style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 30 }}
      >
        <Kennzahl wert={gesamt} name="Bände im Regal" />
        <Kennzahl wert={ohneJahr} name="ohne Lesedatum" />
        {werte && <Kennzahl wert={werte.favs} name="Lieblingsbücher" />}
        {werte && (
          <Kennzahl
            wert={werte.avgRating != null ? `${werte.avgRating}/10` : "—"}
            name={`Schnitt aus ${werte.rated} Bewertungen`}
          />
        )}
        {werte && werte.pages > 0 && (
          <Kennzahl
            wert={werte.pages.toLocaleString("de-DE")}
            name="Seiten insgesamt"
          />
        )}
      </div>

      {jahre.length > 0 && (
        <>
          <h3 className="brettname">Jahresringe</h3>
          <Balken jahre={jahre} />
        </>
      )}

      {werte && (
        <>
          <h3 className="brettname">
            Meistgelesene Autorinnen und Autoren <em>{werte.authors.length}</em>
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {werte.authors.map((a) => (
              <span key={a.author} className="chip">
                {a.author} <span style={{ opacity: 0.55 }}>{a.count}</span>
              </span>
            ))}
          </div>

          {werte.longestSeries && (
            <p className="lauftext" style={{ marginTop: 26 }}>
              Längste Reihe im Regal:{" "}
              <strong>{werte.longestSeries.name}</strong> mit{" "}
              {werte.longestSeries.count} Bänden.
            </p>
          )}

          {werte.tags.length > 0 && (
            <>
              <h3 className="brettname">Häufigste Schlagworte</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {werte.tags.slice(0, 24).map((t) => (
                  <span key={t.name} className="chip">
                    {t.name} <span style={{ opacity: 0.55 }}>{t.count}</span>
                  </span>
                ))}
              </div>
            </>
          )}

          <h3 className="brettname">Vollständigkeit</h3>
          <p className="lauftext">
            {werte.withCover} von {werte.total} mit Umschlag ·{" "}
            {werte.withDescription} mit Inhaltsangabe · {werte.tagged}{" "}
            verschlagwortet. Was fehlt, steht in der Werkstatt.
          </p>
        </>
      )}
    </>
  );
}
